import React from 'react';
import { Lock } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ItemTitle } from '@/components/ui/item';
import ReportAreaScopeBar from 'components/Reports/automated/ReportAreaScopeBar';
import type { useReportAreaScope } from 'components/Reports/automated/useReportAreaScope';
import {
  SitrepCardSurface,
  type GetSitrepsResponse,
} from 'components/Reports/reports.types';
import { formatInBrowserTimeZone } from 'components/Reports/section/sectionLastUpdated';
import { useReportVersioningFlag } from 'utils/featureFlags';
import { useAorText } from 'utils/aorTerminology';

type SitrepCardHeaderProps = {
  sitrep: GetSitrepsResponse;
  surface?: SitrepCardSurface;
  areaScope: ReturnType<typeof useReportAreaScope>;
  canEditArea?: boolean;
  trailing?: React.ReactNode;
  includeTitle?: boolean;
  includeScopeBar?: boolean;
};

/** Shared header chrome for all SITREP card surfaces. */
const SitrepCardHeader: React.FC<SitrepCardHeaderProps> = ({
  sitrep,
  surface,
  areaScope,
  canEditArea = false,
  trailing,
  includeTitle = true,
  includeScopeBar = true,
}) => {
  const reportVersioning = useReportVersioningFlag();
  const aorText = useAorText();
  const isPendingReviewDraft =
    surface === SitrepCardSurface.Draft && sitrep.approval_status === 'PENDING';
  const isRejectedDraft =
    surface === SitrepCardSurface.Draft &&
    sitrep.approval_status === 'REJECTED';

  const statusConfig =
    surface === SitrepCardSurface.Review
      ? {
          key: 'pending-approval' as const,
          label: 'Pending Approval',
        }
      : isPendingReviewDraft
        ? {
            key: 'pending-review' as const,
            label: sitrep.pending_reviewer_names
              ? `Pending Review by ${sitrep.pending_reviewer_names}`
              : 'Pending Review',
          }
        : isRejectedDraft
          ? {
              key: 'draft-rejected' as const,
              label: sitrep.rejected_reviewer_names
                ? `Draft Rejected by ${sitrep.rejected_reviewer_names}`
                : 'Draft Rejected',
            }
          : surface === SitrepCardSurface.Draft
            ? {
                key: 'draft' as const,
                label: 'Draft',
              }
            : null;

  const areaKind =
    areaScope.subjectKind === 'asset' || areaScope.subjectKind === 'custom'
      ? areaScope.subjectKind
      : sitrep.incident_id
        ? 'incident'
        : 'aor';
  const isNamedSubject = areaKind === 'asset' || areaKind === 'custom';
  const areaLabel =
    (isNamedSubject ? areaScope.subjectLabel : null) ||
    areaScope.effectiveScope?.spatialFilterLabel ||
    areaScope.subjectLabel ||
    'No area — lists are unscoped';

  const statusBadge = statusConfig ? (
    <Badge
      variant={
        statusConfig.key === 'draft-rejected'
          ? 'outline'
          : statusConfig.key === 'draft'
            ? 'orange'
            : 'blue'
      }
      className={
        statusConfig.key === 'draft-rejected'
          ? 'tw:h-auto tw:whitespace-normal tw:border-destructive/40 tw:bg-destructive/10 tw:text-destructive tw:dark:border-red-800 tw:dark:bg-red-950 tw:dark:text-red-200'
          : statusConfig.key === 'draft'
            ? undefined
            : 'tw:dark:border-blue-700 tw:dark:bg-blue-950 tw:dark:text-blue-200'
      }
    >
      {statusConfig.label}
    </Badge>
  ) : null;

  return (
    <div className="tw:flex tw:w-full tw:min-w-0 tw:flex-col tw:gap-1">
      {includeTitle ? (
        <>
          <div className="tw:flex tw:items-start tw:justify-between tw:gap-2">
            <ItemTitle className="tw:line-clamp-none tw:w-auto tw:max-w-full tw:flex-wrap tw:text-base tw:text-foreground">
              <Badge variant="outline">{aorText(areaLabel)}</Badge>
            </ItemTitle>
          </div>
          {!reportVersioning &&
          sitrep.approval_status === 'APPROVED' &&
          sitrep.approvals?.[0] ? (
            <Alert className="tw:min-w-0 tw:border-success tw:bg-success/10 tw:px-3 tw:py-2">
              <Lock />
              <AlertDescription className="tw:text-xs tw:font-normal tw:text-emerald-200">
                Approved:{' '}
                {sitrep.approvals[0].approved_at
                  ? formatInBrowserTimeZone(sitrep.approvals[0].approved_at)
                  : ''}{' '}
                by {sitrep.approvals[0].approved_by_user_name}
              </AlertDescription>
            </Alert>
          ) : null}
        </>
      ) : null}
      {includeScopeBar ? (
        <ReportAreaScopeBar
          leading={statusBadge}
          defaultScope={areaScope.defaultScope}
          customScope={areaScope.customScope}
          isEditingArea={areaScope.isEditingArea}
          areaKind={areaKind}
          assetRadiusKm={areaScope.assetRadiusKm}
          onAssetRadiusChange={areaScope.handleAssetRadiusChange}
          disabled={!canEditArea}
          trailing={trailing}
          onEditOnMap={areaScope.handleEditOnMap}
          onCancelDraw={areaScope.handleCancelDraw}
          onResetScope={areaScope.handleResetScope}
        />
      ) : null}
    </div>
  );
};

export default SitrepCardHeader;
